import * as signalR from '@microsoft/signalr';
import env from '../env';

const COMMUNITY_HUB_URL = env.SIGNALR_URL.replace('/chathub', '/communityHub');

export interface CommunityMessage {
  id: number;
  senderId: number;
  senderName: string;
  senderType: number;
  content: string;
  messageType: number;
  messageTag?: number;
  createdAt: string;
  isDeleted: boolean;
  attachments: CommunityAttachment[];
}

export interface CommunityAttachment {
  id: number;
  fileName: string;
  filePath: string;
  fileSize: number;
  fileType: string;
}

export interface OnlineUser {
  userId: number;
  userType: string;
  userName: string;
}

export interface CommunityUserTyping {
  userId: number;
  userType: string;
  userName: string;
}

// SignalR Service for Community Chat
class CommunitySignalRService {
  private connection: signalR.HubConnection | null = null;
  private listeners: Map<string, Set<(data: any) => void>> = new Map();
  private isConnecting: boolean = false;

  async connect(): Promise<void> {
    if (this.connection?.state === signalR.HubConnectionState.Connected) {
      return;
    }

    if (this.isConnecting) {
      // Wait for existing connection attempt
      return new Promise((resolve) => {
        const checkConnection = setInterval(() => {
          if (this.connection?.state === signalR.HubConnectionState.Connected) {
            clearInterval(checkConnection);
            resolve();
          }
        }, 100);
      });
    }

    this.isConnecting = true;
    const token = sessionStorage.getItem('token');

    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(COMMUNITY_HUB_URL, {
        accessTokenFactory: () => token || ''
      })
      .withAutomaticReconnect()
      .build();

    // Register event handlers that emit to all listeners
    this.connection.on('ReceiveCommunityMessage', (data: CommunityMessage) => {
      //console.log('SignalR ReceiveCommunityMessage:', data);
      this.emit('ReceiveCommunityMessage', data);
    });

    this.connection.on('UserJoinedCommunity', (data: OnlineUser) => {
      //console.log('User joined community:', data);
      this.emit('UserJoinedCommunity', data);
    });

    this.connection.on('UserLeftCommunity', (data: OnlineUser) => {
      //console.log('User left community:', data);
      this.emit('UserLeftCommunity', data);
    });

    this.connection.on('UserTyping', (data: CommunityUserTyping) => {
      //console.log('User typing in community:', data);
      this.emit('UserTyping', data);
    });

    this.connection.on('UserStoppedTyping', (data: CommunityUserTyping) => {
      //console.log('User stopped typing in community:', data);
      this.emit('UserStoppedTyping', data);
    });

    this.connection.on('OnlineUsersUpdated', (users: OnlineUser[]) => {
      //console.log('Online users updated:', users);
      this.emit('OnlineUsersUpdated', users);
    });

    this.connection.on('MessageDeleted', (messageId: number) => {
      //console.log('Message deleted:', messageId);
      this.emit('MessageDeleted', messageId);
    });

    this.connection.on('Error', (error: string) => {
      console.error('Community Hub Error:', error);
      this.emit('Error', error);
    });

    try {
      await this.connection.start();
      //console.log('Connected to CommunityHub');
      this.isConnecting = false;
    } catch (err) {
      console.error('CommunityHub connection error:', err);
      this.isConnecting = false;
      throw err;
    }
  }

  async disconnect(): Promise<void> {
    if (this.connection) {
      await this.connection.stop();
      this.connection = null;
    }
    // Clear all listeners on disconnect
    this.listeners.clear();
  }

  on(event: string, callback: (data: any) => void): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(callback);
  }

  off(event: string, callback: (data: any) => void): void {
    const listeners = this.listeners.get(event);
    if (listeners) {
      listeners.delete(callback);
    }
  }

  private emit(event: string, data: any): void {
    const listeners = this.listeners.get(event);
    if (listeners) {
      listeners.forEach(callback => {
        try {
          callback(data);
        } catch (err) {
          console.error(`Error in ${event} listener:`, err);
        }
      });
    }
  }

  async joinCommunity(): Promise<void> {
    if (this.connection?.state === signalR.HubConnectionState.Connected) {
      await this.connection.invoke('JoinCommunity');
    }
  }

  async leaveCommunity(): Promise<void> {
    if (this.connection?.state === signalR.HubConnectionState.Connected) {
      await this.connection.invoke('LeaveCommunity');
    }
  }

  async sendMessage(content: string, messageTag?: number, attachmentPath?: string): Promise<void> {
    if (this.connection?.state === signalR.HubConnectionState.Connected) {
      await this.connection.invoke('SendMessage', content, messageTag, attachmentPath);
    }
  }

  async deleteMessage(messageId: number): Promise<void> {
    if (this.connection?.state === signalR.HubConnectionState.Connected) {
      await this.connection.invoke('DeleteMessage', messageId);
    }
  }

  async startTyping(): Promise<void> {
    if (this.connection?.state === signalR.HubConnectionState.Connected) {
      await this.connection.invoke('StartTyping');
    }
  }

  async stopTyping(): Promise<void> {
    if (this.connection?.state === signalR.HubConnectionState.Connected) {
      await this.connection.invoke('StopTyping');
    }
  }

  async getOnlineUsers(): Promise<OnlineUser[]> {
    if (this.connection?.state === signalR.HubConnectionState.Connected) {
      const users = await this.connection.invoke('GetOnlineUsers');
      return users || [];
    }
    return [];
  }

  isConnected(): boolean {
    return this.connection?.state === signalR.HubConnectionState.Connected;
  }
}

// Export singleton instance
export const communitySignalR = new CommunitySignalRService();
